<template>
  <div class="datepicker">
    <PageModal
      v-model:isOpen="isModalOpen"
      :headerModel="{
        title: $t`Фильтр`,
        shadowed: true,
      }"
    >
      <template #head>
        <div class="i-close-red-cross"></div>
      </template>
      <template #tail>
        <div v-show="pickedDate" @click="resetDate">
          {{ $t`Сбросить все` }}
        </div>
      </template>
      <div class="datepicker__content">
        <div class="content__options">
          <div class="options__hint">{{ $t`ПОКАЗАТЬ ОПЕРАЦИИ` }}</div>
          <RadioButtons
            v-model="pickedDate"
            :items="dateOptions"
            @selected="setDate"
          >
            <template #item-custom="{ item, isActive }">
              <div class="custom-radio-item__content">
                <div v-if="item.value" class="custom-radio-item__content_label">
                  {{ $t`За период` }}
                </div>
                <div class="custom-radio-item__content_value">
                  {{ item.text }}
                </div>
              </div>
              <div class="custom-radio-item__icon">
                <div
                  :class="{
                    'i-arrow': !isActive,
                    'i-checkbox-filled': isActive,
                  }"
                ></div>
              </div>
            </template>
          </RadioButtons>
        </div>
        <div class="datepicker__actions">
          <CommonButton @click="pickDate">{{ $t`Применить` }}</CommonButton>
        </div>
      </div>
    </PageModal>
    <slot name="activator" :on="{ click: openModal }"></slot>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { captureException } from "@sentry/vue";
import PageModal from "@/components/shared/modal/PageModal.vue";
import CommonButton from "@/components/shared/button/CommonButton.vue";
import RadioButtons from "@/components/shared/radio/RadioButtons.vue";

const nativeCalendarOptions = {
  selectMode: "Range", // Range | Single
  calendarBeginDate: "01.01.2015",
  calendarEndDate: dayjs().format("DD.MM.YYYY"),
  selectedRangeLimit: "year", // week | month | quarter | halfYear | year | twoYear | threeYear
};

const getDatePrintFormat = (date) => {
  let dateFormat = "DD MMMM";
  if (date.year() !== dayjs().year()) {
    dateFormat += " `YY";
  }
  return dateFormat;
};

const getFormattedDates = (from, to) =>
  `${from.format(getDatePrintFormat(from))} - ${to.format(
    getDatePrintFormat(to)
  )}`;

export default {
  components: { PageModal, CommonButton, RadioButtons },
  props: {
    date: {
      type: [Object, null],
      required: true,
    },
  },
  emits: ["update:date"],
  data() {
    return {
      isModalOpen: false,
      pickedDate: null,
      dateOptions: {
        week: {
          text: this.$t`За неделю`,
          value: {
            from: dayjs().subtract(7, "day"),
            to: dayjs(),
          },
        },
        month: {
          text: this.$t`За месяц`,
          value: {
            from: dayjs().subtract(1, "month"),
            to: dayjs(),
          },
        },
        threeMonths: {
          text: this.$t`За три месяца`,
          value: {
            from: dayjs().subtract(3, "month"),
            to: dayjs(),
          },
        },
        halfYear: {
          text: this.$t`За полгода`,
          value: {
            from: dayjs().subtract(6, "month"),
            to: dayjs(),
          },
        },
        year: {
          text: this.$t`За 1 год`,
          value: {
            from: dayjs().subtract(1, "year"),
            to: dayjs(),
          },
        },
        custom: {
          text: this.$t`За период`,
          value: null,
        },
      },
    };
  },
  mounted() {
    window.mobapp_callbacks.onResult = (status, params) => {
      if (status === "SUCCESS") {
        const { selectedStartDate, selectedEndDate } = JSON.parse(params);

        const from = dayjs(selectedStartDate, "DD/MM/YYYY");
        const to = dayjs(selectedEndDate, "DD/MM/YYYY");

        this.dateOptions.custom.text = `${getFormattedDates(from, to)}`;
        this.dateOptions.custom.value = { from, to };
      } else if (status === "CANCELLED") {
        this.resetDate();
      } else if (status === "FAILED") {
        this.resetDate();
        captureException("Ошибка при выборе дат в выписке Kaspi Red");
      }
    };
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    setDate(option) {
      if (option === "custom") {
        this.openCalendar();
      }
    },
    pickDate() {
      if (
        !this.pickedDate ||
        (this.pickedDate === "custom" && !this.dateOptions.custom.value)
      ) {
        this.pickedDate = "month";
      }
      this.$emit("update:date", { ...this.dateOptions[this.pickedDate].value });
      this.closeModal();
    },
    resetDate() {
      this.pickedDate = null;
      this.dateOptions.custom.value = null;
      this.dateOptions.custom.text = this.$t`За период`;
    },
    openCalendar() {
      if (window.kaspi?.send) {
        window.kaspi.send(
          "openCalendar",
          JSON.stringify(nativeCalendarOptions),
          "mobapp_callbacks.onResult"
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-radio-item {
  &__content {
    position: relative;

    &_label {
      text-align: left;
      font-size: 12px;
      line-height: 14px;
      color: #a8a8a8;
    }
  }
}

.datepicker {
  &__content {
    width: 100%;
    height: 100vh;
    background-color: #f0f0f0;
  }

  &__actions {
    padding: 15px;
  }
}

.options__hint {
  text-align: left;
  font-size: 12px;
  color: #a8a8a8;
  padding: 16px 15px 10px;
}
</style>
